import { COMPANY } from '@/lib/company';

interface ContactDetailsProps {
  className?: string;
}

/**
 * Phone, email and postal address — the contact block the Kundtjänst dropdown
 * and the Kundtjänst sidebar both carry.
 *
 * No hooks and no translation call of its own, so it renders in a server
 * component and inside the client-side dropdown without either needing a
 * boundary it does not already have.
 *
 * Deliberately no "Svar inom 24 h" under the address: Torsbo asked for it to go
 * (p.14 of the "Hemsidan 2.0" feedback). It was a response-time promise nothing
 * in the shop actually guarantees, and the phone number above it is the honest
 * answer for anyone in a hurry.
 *
 * The wrapper is the caller's: the dropdown draws this as a tinted column with
 * a left rule, the sidebar as a bordered card, and forcing one chrome on both
 * would make it fit neither. What is shared is the content and its type scale.
 *
 * Deliberately no opening hours. The canvas prints "Vard 9–18, lör 10–14" here,
 * but hours are authored in the `opening_hours` CMS block precisely so a
 * changed Saturday does not need a deploy — see lib/opening-hours.ts. A second,
 * frozen copy in a sidebar is exactly the thing that goes stale unnoticed.
 */
export function ContactDetails({ className = '' }: ContactDetailsProps) {
  return (
    <div className={`flex flex-col gap-3.5 ${className}`}>
      <a
        href={`tel:${COMPANY.phone.replace(/[^\d+]/g, '')}`}
        className="text-[15px] font-bold tabular-nums text-foreground transition-colors hover:text-accent-foreground"
      >
        {COMPANY.phone}
      </a>
      <div className="flex flex-col gap-0.5">
        <a
          href={`mailto:${COMPANY.email}`}
          className="break-all text-[13px] font-bold text-foreground transition-colors hover:text-accent-foreground"
        >
          {COMPANY.email}
        </a>
      </div>
      <address className="mt-auto border-t border-border pt-3 text-xs not-italic leading-relaxed text-muted">
        {COMPANY.street}
        <br />
        {COMPANY.postalCode} {COMPANY.city}
      </address>
    </div>
  );
}
